import { useState, useEffect } from "react";
import {
  Bars3Icon,
  XMarkIcon,
  UserCircleIcon,
} from "@heroicons/react/24/solid";
import { NavLink, useNavigate } from "react-router-dom";
import useMediaQuery from "../../../hooks/useMediaQuery";
import ActionButton from "../../../shared/ActionButton";
import Logo from "../../../shared/design/Logo";
import {
  useSendLogoutMutation,
  useCheckLoginMutation,
} from "../../auth/authApiSlice";
import { useAppDispatch } from "../../../hooks/typedHooks";
import { PulseLoader } from "react-spinners";

type Props = {
  isTopOfPage: boolean;
};

// Shared link styling: lime-underline hover (.wn-link) + teal active state.
const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  `wn-link text-[14.5px] font-medium transition-colors ${
    isActive ? "font-bold text-secondary-400" : "text-ink hover:text-ink"
  }`;

// Light variant for the dark mobile drawer.
const mobileNavLinkClass = ({ isActive }: { isActive: boolean }) =>
  `wn-link transition-colors ${
    isActive ? "text-primary-500" : "text-sand hover:text-primary-300"
  }`;

const Navbar = ({ isTopOfPage }: Props) => {
  const flexBetween = "flex items-center justify-between";
  const [isMenuToggled, setIsMenuToggled] = useState<boolean>(false);
  const isAboveMediumScreens = useMediaQuery("(min-width: 1060px)");
  // Transparent over the hero at the top; translucent sand bar once scrolled.
  const navbarBackground = isTopOfPage
    ? "bg-transparent py-5"
    : "bg-sand/85 py-4 shadow-[0_1px_0_rgba(0,0,0,0.06)] backdrop-blur-md";
  const [content, setContent] = useState(<PulseLoader size={8} />);
  const [mobileContent, setMobileContent] = useState(<PulseLoader size={8} />);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [sendLogout, { isSuccess: isSuccessLogout }] = useSendLogoutMutation();

  const logoutAction = () => {
    sendLogout("");
  };

  const toggleMobileNav = () => setIsMenuToggled(!isMenuToggled);

  const [
    sendCheckLogin,
    {
      data: dataCheckLogin,
      isLoading: isLoadingCheckLogin,
      isSuccess: isSuccessCheckLogin,
      isError: isErrorCheckLogin,
      error: errorCheckLogin,
    },
  ] = useCheckLoginMutation();

  useEffect(() => {
    if (isSuccessLogout) {
      navigate("/");
      navigate(0);
    }
  }, [isSuccessLogout, navigate]);

  useEffect(() => {
    if (isLoadingCheckLogin) setContent(<PulseLoader size={8} />);

    if (isErrorCheckLogin) {
      console.log(errorCheckLogin);
      if (errorCheckLogin) {
        if ("data" in errorCheckLogin) {
          setContent(<p className="text-hard">Unable to load session</p>);
        }
      }
    }

    if (isSuccessCheckLogin) {
      setContent(
        dataCheckLogin?.data.loginStatus ? (
          <div className="flex items-center gap-6">
            <NavLink
              to="/"
              onClick={logoutAction}
              className="wn-link text-[14.5px] font-medium text-muted transition-colors hover:text-ink"
            >
              Log out
            </NavLink>
            <NavLink to="/manage-account" aria-label="Manage account">
              <UserCircleIcon className="h-8 w-8 fill-secondary-400 transition-colors hover:fill-ink" />
            </NavLink>
          </div>
        ) : (
          <div className="flex items-center gap-6">
            <NavLink
              to="login"
              className="text-[14.5px] font-medium text-muted transition-colors hover:text-ink"
            >
              Log in
            </NavLink>
            <ActionButton target={"signup"} variant="ink">
              Sign up
            </ActionButton>
          </div>
        )
      );
      setMobileContent(
        dataCheckLogin?.data.loginStatus ? (
          <>
            <NavLink
              to="manage-account"
              onClick={() => setIsMenuToggled(false)}
              className={mobileNavLinkClass}
            >
              Manage Account
            </NavLink>
            <NavLink
              to="/"
              onClick={logoutAction}
              className={mobileNavLinkClass}
            >
              Log out
            </NavLink>
          </>
        ) : (
          <>
            <NavLink
              to="login"
              onClick={() => setIsMenuToggled(false)}
              className={mobileNavLinkClass}
            >
              Log in
            </NavLink>
            <NavLink
              to="signup"
              onClick={() => setIsMenuToggled(false)}
              className={mobileNavLinkClass}
            >
              Sign up
            </NavLink>
          </>
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isSuccessCheckLogin,
    dataCheckLogin,
    isLoadingCheckLogin,
    isErrorCheckLogin,
    errorCheckLogin,
    dispatch,
    sendLogout,
  ]);

  useEffect(() => {
    sendCheckLogin("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <nav>
      <div
        className={`${navbarBackground} ${flexBetween} fixed top-0 z-30 w-full px-6 transition-all duration-300 md:px-12`}
      >
        <div className={`${flexBetween} mx-auto w-full max-w-[1320px]`}>
          {/* Left: brand mark */}
          <Logo />

          {/* Right */}
          {isAboveMediumScreens ? (
            <div className="flex items-center gap-[34px]">
              <NavLink to="tours" end className={navLinkClass}>
                Tours
              </NavLink>
              <NavLink to="people" className={navLinkClass}>
                Our Team
              </NavLink>
              <NavLink to="contact" className={navLinkClass} state={"general"}>
                Contact
              </NavLink>
              {content}
            </div>
          ) : (
            <button
              className="rounded-full bg-secondary-400 p-2"
              onClick={() => setIsMenuToggled(!isMenuToggled)}
              aria-label="Open menu"
            >
              <Bars3Icon className="h-6 w-6 text-white" />
            </button>
          )}
        </div>
      </div>

      {/* MOBILE MENU MODAL */}
      {!isAboveMediumScreens && isMenuToggled && (
        <div className="fixed bottom-0 right-0 z-40 h-full w-10/12 bg-ink text-sand drop-shadow-xl">
          {/* CLOSE ICON */}
          <div className="flex justify-end p-12">
            <button onClick={toggleMobileNav} aria-label="Close menu">
              <XMarkIcon className="h-6 w-6 text-muted-300" />
            </button>
          </div>

          {/* MENU ITEMS */}
          <div className="ml-[33%] flex flex-col gap-10 text-2xl">
            <NavLink
              to="/"
              onClick={toggleMobileNav}
              className={mobileNavLinkClass}
            >
              Home
            </NavLink>
            <NavLink
              to="tours"
              onClick={toggleMobileNav}
              className={mobileNavLinkClass}
            >
              Tours
            </NavLink>
            <NavLink
              to="people"
              onClick={toggleMobileNav}
              className={mobileNavLinkClass}
            >
              Our Team
            </NavLink>
            <NavLink
              to="contact"
              onClick={toggleMobileNav}
              className={mobileNavLinkClass}
            >
              Contact
            </NavLink>
            {mobileContent}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
